import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import { FiUpload, FiTrash2, FiImage, FiPlus, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const AdminGallery = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ image: null, caption: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setImages(data.data);
            }
        } catch (error) {
            console.error("Error fetching gallery:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.image) return;

        setUploading(true);
        const data = new FormData();
        data.append('image', formData.image);
        data.append('caption', formData.caption);

        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php`, {
                method: 'POST',
                body: data
            });
            const result = await response.json();
            if (result.status === 'success') {
                alert("Image uploaded successfully!");
                setShowModal(false);
                setFormData({ image: null, caption: '' });
                fetchGallery();
            } else {
                alert(result.message);
            }
        } catch (error) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manage_gallery.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (result.status === 'success') {
                setImages(images.filter(img => img.id !== id));
            }
        } catch (error) {
            alert("Delete failed");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-slate-800">Campus Gallery</h3>
                <button 
                    onClick={() => setShowModal(true)}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-orange-600 transition-colors shadow-sm"
                >
                    <FiPlus /> Add Image
                </button>
            </div>

            {loading ? (
                <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {images.map(img => (
                        <div key={img.id} className="relative group rounded-xl overflow-hidden border border-slate-100 shadow-sm">
                            <img 
                                src={`${API_BASE_URL}/../${img.image_path}`} 
                                alt={img.caption}
                                className="w-full h-40 object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button 
                                    onClick={() => handleDelete(img.id)}
                                    className="p-2 bg-white text-red-500 rounded-full hover:bg-red-50 transition-colors"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-slate-800">Upload New Image</h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><FiX size={20} /></button>
                        </div>
                        <form onSubmit={handleUpload} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Image File</label>
                                <input 
                                    type="file" 
                                    accept="image/*"
                                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                                    onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Caption</label>
                                <input 
                                    type="text" 
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-orange-500 outline-none"
                                    value={formData.caption}
                                    onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                                />
                            </div>
                            <button 
                                type="submit"
                                disabled={uploading}
                                className="w-full bg-orange-500 text-white py-3 rounded-lg font-bold hover:bg-orange-600 transition-colors disabled:opacity-50"
                            >
                                {uploading ? 'Uploading...' : 'Upload Image'}
                            </button>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AdminGallery;
